import {
  BracesIcon,
  CircleCheckIcon,
  ClipboardCheckIcon,
  FileTextIcon,
  FunnelIcon,
  ImportIcon,
  LayersIcon,
  LinkIcon,
  SettingsIcon,
  SquarePenIcon,
  TableIcon,
  type LucideIcon,
} from "lucide-react";

import { icons } from "@/objects/@icons";

// ── Types ────────────────────────────────────────────────────────────────────

export type TreeData = {
  // ``unknown[]`` is intentionally permissive — the static demo content
  // includes mixed arrays of objects/strings that Tree renders via
  // JSON.stringify; tightening this further forces every demo dataset
  // to be re-shaped, which provides no runtime value.
  [key: string]: string | number | boolean | string[] | TreeData | unknown[];
};

export type SubCallDef = {
  toolCallName: string;
  Icon: LucideIcon;
  input?: TreeData;
  thinking?: string;
  output?: TreeData;
  stats?: { added?: number; updated?: number; removed?: number };
  mode?: "sequential" | "parallel" | "loop";
  subCalls?: SubCallDef[];
};

export type FlowNodeDef = {
  id: string;
  title: string;
  Icon: LucideIcon;
  input?: TreeData;
  thinking?: string;
  output?: TreeData;
  stats?: { added?: number; updated?: number; removed?: number };
  mode?: "sequential" | "parallel" | "loop";
  subCalls?: SubCallDef[];
};

export type FlowStepDef = {
  activeNodeId: string;
  toolCallName: string;
  preText: string;
  postText: string;
  preDurationMs: number;
  animDurationMs: number;
  postDurationMs: number;
  pauseMs: number;
};

export type AgentScenario = {
  title: string;
  subtitle: string;
  nodes: FlowNodeDef[];
  steps: FlowStepDef[];
  getNodeColor: (nodeId: string) => string;
};

// ── Scenario ─────────────────────────────────────────────────────────────────

const AGENT_SCENARIO: AgentScenario = {
  title: "Intercompany Reconciliation & Consolidation",
  subtitle:
    "A main orchestrator agent delegates to specialized sub-agents, routing through conditions and collecting results into consolidated financial statements.",

  nodes: [
    {
      id: "orchestrator",
      title: "Orchestrator",
      Icon: SettingsIcon,
      input: {
        task_id: "recon-q4-2024",
        entities: ["entity_a", "entity_b", "entity_c"],
        period: "Q4-2024",
        document_count: 47,
      },
      output: {
        status: "complete",
        consolidated_statements: ["P&L", "Balance Sheet", "Cash Flow"],
      },
    },
    {
      id: "ingestion",
      title: "Ingestion Agent",
      Icon: ImportIcon,
      input: {
        task_id: "ingest-001",
        source_documents: {
          total: 47,
          types: ["invoices", "receipts", "bank_statements"],
        },
        entities: ["Acme Corp (A)", "Baker Ltd (B)", "Carter Inc (C)"],
      },
      output: {
        records_created: 47,
        by_entity: {
          entity_a: { id: "ent_a", records: 18, total: "$142,000" },
          entity_b: { id: "ent_b", records: 21, total: "$98,000" },
          entity_c: { id: "ent_c", records: 8, total: "$34,000" },
        },
        flags: { duplicates: ["INV-1092"] },
      },
      stats: { added: 47, updated: 1, removed: 0 },
      mode: "loop",
      subCalls: [
        {
          toolCallName: "Process Entity A",
          Icon: FileTextIcon,
          input: {
            entity: { id: "ent_a", name: "Acme Corp" },
            documents: { count: 18, type: "invoices" },
          },
          thinking:
            "Parsing 18 invoices… OCR on 3 scanned receipts… Flagging duplicate INV-1092…",
          output: {
            records: 18,
            total: "$142,000",
            flags: { duplicate: "INV-1092" },
          },
          stats: { added: 18, updated: 1, removed: 0 },
        },
        {
          toolCallName: "Process Entity B",
          Icon: FileTextIcon,
          input: {
            entity: { id: "ent_b", name: "Baker Ltd" },
            documents: { count: 21, type: "invoices" },
          },
          thinking: "Parsing 21 digital invoices… Classifying by vendor…",
          output: { records: 21, total: "$98,000" },
          stats: { added: 21, updated: 0, removed: 0 },
        },
        {
          toolCallName: "Process Entity C",
          Icon: FileTextIcon,
          input: {
            entity: { id: "ent_c", name: "Carter Inc" },
            documents: { count: 8, type: "mgmt_fee_statements" },
          },
          thinking: "Parsing 8 management fee statements…",
          output: { records: 8, total: "$34,000" },
          stats: { added: 8, updated: 0, removed: 0 },
        },
      ],
    },
    {
      id: "detection",
      title: "IC Detection",
      Icon: icons.search,
      input: {
        task_id: "detect-001",
        source: "ingestion output",
        record_count: 47,
        entity_registry: "registry_v3",
      },
      output: {
        ic_transactions: 12,
        matched: 10,
        mismatches: {
          count: 2,
          items: ["AR #4401 ($750 gap)", "Fee #MF-09 ($700 gap)"],
        },
        pairs: {
          A_to_B: { count: 5, total: "$67,200" },
          B_to_C: { count: 4, total: "$31,800" },
          C_to_A: { count: 3, total: "$34,000" },
        },
      },
      stats: { added: 12, updated: 0, removed: 0 },
      mode: "sequential",
      subCalls: [
        {
          toolCallName: "Find Counterparties",
          Icon: LinkIcon,
          input: {
            records: 47,
            registry: "entity_registry_v3",
            match_strategy: "fuzzy + alias lookup",
          },
          thinking:
            'Mapping "Acme Holdings Sub-A" → Entity A… Cross-referencing entity registry…',
          output: {
            ic_pairs_found: 12,
            alias_resolved: { "Acme Holdings Sub-A": "ent_a" },
          },
          stats: { added: 12, updated: 0, removed: 0 },
        },
        {
          toolCallName: "Match Transactions",
          Icon: ClipboardCheckIcon,
          input: {
            ic_pairs: 12,
            tolerance: "$0.01",
          },
          thinking:
            "Matching AR ↔ AP across entities… 10 exact matches. 2 amount mismatches flagged.",
          output: {
            exact_matches: 10,
            mismatches: [
              {
                ar: { id: "AR-4401", amount: "$15,000" },
                ap: { id: "AP-7823", amount: "$14,250" },
                gap: "$750",
              },
              {
                fee: { id: "MF-09", amount: "$11,500" },
                accrual: "$10,800",
                gap: "$700",
              },
            ],
          },
        },
      ],
    },
    {
      id: "condition",
      title: "Evaluate Condition",
      Icon: FunnelIcon,
      input: {
        expression: "mismatches.length > 0",
        value: true,
      },
      thinking: "Evaluating: 2 mismatches detected. Routing…",
    },
    {
      id: "reconciler",
      title: "Reconciler",
      Icon: BracesIcon,
      input: {
        task_id: "recon-002",
        mismatches: [
          {
            pair: "AR-4401 / AP-7823",
            gap: "$750",
          },
          {
            pair: "MF-09 / Accrual",
            gap: "$700",
          },
        ],
      },
      output: {
        journal_entries: 2,
        net_variance: "$0.00",
        entries: [
          {
            id: "JE-801",
            debit: "Sales Discount",
            amount: "$750",
            entity: "A",
          },
          { id: "JE-802", debit: "Accrual Adj", amount: "$700", entity: "A" },
        ],
      },
      stats: { added: 2, updated: 2, removed: 0 },
      mode: "sequential",
      subCalls: [
        {
          toolCallName: "Investigate Gaps",
          Icon: icons.search,
          mode: "loop",
          input: { mismatches: 2 },
          output: { causes_identified: 2 },
          subCalls: [
            {
              toolCallName: "AR #4401 vs AP #7823",
              Icon: icons.search,
              input: {
                receivable: { id: "AR-4401", entity: "A", amount: "$15,000" },
                payable: { id: "AP-7823", entity: "B", amount: "$14,250" },
                gap: "$750",
              },
              thinking:
                "Entity B applied 5% early-payment discount not recorded by Entity A…",
              output: {
                cause: "Early-payment discount (5%)",
                resolution: "DR Sales Discount $750",
              },
            },
            {
              toolCallName: "Fee #MF-09 vs Accrual",
              Icon: icons.search,
              input: {
                fee: { id: "MF-09", entity: "C", amount: "$11,500" },
                accrual: { entity: "A", amount: "$10,800" },
                gap: "$700",
              },
              thinking:
                "Entity A accrued based on prior-month estimate, actual is $700 higher…",
              output: {
                cause: "Stale accrual estimate",
                resolution: "ADJ accrual → actual $700",
              },
            },
          ],
        },
        {
          toolCallName: "Create Journal Entries",
          Icon: SquarePenIcon,
          input: { discrepancies: 2, target_entity: "A" },
          thinking:
            "DR Sales Discount $750 (Entity A)… ADJ accrual → actual $700 (Entity A)…",
          output: {
            posted: [
              { id: "JE-801", type: "DR Sales Discount", amount: "$750" },
              { id: "JE-802", type: "ADJ Accrual", amount: "$700" },
            ],
          },
          stats: { added: 2, updated: 2, removed: 0 },
        },
      ],
    },
    {
      id: "direct-post",
      title: "Direct Post",
      Icon: CircleCheckIcon,
      input: { matched_entries: 10, target: "General Ledger" },
      thinking: "Posting 10 matched entries to general ledger…",
      output: { gl_entries_posted: 10, status: "success" },
      stats: { added: 10, updated: 0, removed: 0 },
    },
    {
      id: "consolidation",
      title: "Consolidation",
      Icon: FileTextIcon,
      input: {
        task_id: "consol-001",
        ic_transactions: 12,
        nci: { entity: "C", percentage: "15%" },
        output_format: ["P&L", "Balance Sheet", "Cash Flow"],
      },
      output: {
        elimination_jes: 12,
        financials: {
          revenue: "$2.4M",
          net_income: "$312K",
          nci_adjustment: "$4,200",
        },
        trial_balance: "verified",
      },
      stats: { added: 12, updated: 1, removed: 0 },
      mode: "sequential",
      subCalls: [
        {
          toolCallName: "Generate Eliminations",
          Icon: LayersIcon,
          mode: "parallel",
          input: { transactions: 12 },
          output: { elimination_jes: 12 },
          stats: { added: 12, updated: 0, removed: 0 },
          subCalls: [
            {
              toolCallName: "Eliminate AR/AP",
              Icon: SquarePenIcon,
              input: {
                category: "IC Receivables & Payables",
                entries: 5,
              },
              thinking: "Eliminating intercompany AR/AP totaling $133K…",
              output: { eliminated: "$133K", jes: 5 },
              stats: { added: 5, updated: 0, removed: 0 },
            },
            {
              toolCallName: "Eliminate Revenue/COGS",
              Icon: SquarePenIcon,
              input: {
                category: "IC Revenue & COGS",
                entries: 4,
              },
              thinking:
                "Eliminating intercompany revenue & COGS totaling $98.5K…",
              output: { eliminated: "$98.5K", jes: 4 },
              stats: { added: 4, updated: 0, removed: 0 },
            },
            {
              toolCallName: "Eliminate Mgmt Fees",
              Icon: SquarePenIcon,
              input: {
                category: "IC Management Fees",
                entries: 3,
              },
              thinking: "Eliminating management fee charges totaling $34K…",
              output: { eliminated: "$34K", jes: 3 },
              stats: { added: 3, updated: 0, removed: 0 },
            },
          ],
        },
        {
          toolCallName: "Apply NCI Adjustment",
          Icon: BracesIcon,
          input: {
            entity: { id: "ent_c", name: "Carter Inc" },
            nci_percentage: "15%",
          },
          thinking: "Computing minority interest share… $4,200 adjustment…",
          output: { adjustment: "$4,200", applied_to: "ent_c" },
          stats: { added: 0, updated: 1, removed: 0 },
        },
        {
          toolCallName: "Verify Trial Balance",
          Icon: ClipboardCheckIcon,
          input: {
            ledger: "Consolidated with eliminations",
            check: "debits == credits",
          },
          thinking: "Summing all debits and credits… Checking rounding…",
          output: { balanced: true, status: "verified" },
        },
      ],
    },
  ],

  steps: [
    {
      activeNodeId: "ingestion",
      toolCallName: "Extract Documents",
      preText:
        "I need to extract and classify all uploaded documents for Q4 close across Acme Corp, Baker Ltd, and Carter Inc. I'll process each entity in a loop.",
      postText:
        "Ingestion complete. Entity A: 18 invoices ($142K), Entity B: 21 invoices ($98K), Entity C: 8 mgmt-fee statements ($34K). 1 duplicate flagged on inv #1092.",
      preDurationMs: 3000,
      animDurationMs: 5000,
      postDurationMs: 3500,
      pauseMs: 600,
    },
    {
      activeNodeId: "detection",
      toolCallName: "Find Intercompany Transactions",
      preText:
        "Now I need to identify which transactions are intercompany. I'll first find counterparties, then match transactions.",
      postText:
        "Found 12 IC transactions: A→B 5 service invoices ($67,200), B→C 4 inventory transfers ($31,800), C→A 3 mgmt fees ($34,000). 10 matched cleanly, 2 mismatches with $750 and $700 gaps.",
      preDurationMs: 3000,
      animDurationMs: 4500,
      postDurationMs: 3500,
      pauseMs: 600,
    },
    {
      activeNodeId: "condition",
      toolCallName: "Evaluate Match Results",
      preText:
        "Checking whether any mismatches were found to determine routing.",
      postText:
        "2 mismatches detected — routing to Reconciler. 10 clean matches routed to Direct Post for immediate GL entry.",
      preDurationMs: 1800,
      animDurationMs: 1800,
      postDurationMs: 2500,
      pauseMs: 600,
    },
    {
      activeNodeId: "reconciler",
      toolCallName: "Resolve Discrepancies",
      preText:
        "Two discrepancies need resolution. I'll investigate each gap in a loop, then create the correcting journal entries.",
      postText:
        "$750 gap: 5% early-payment discount not yet recorded by Entity A. $700 gap: prior-month accrual estimate adjusted to actual. 2 correcting JEs posted. Net variance: $0.00.",
      preDurationMs: 3000,
      animDurationMs: 5000,
      postDurationMs: 3500,
      pauseMs: 600,
    },
    {
      activeNodeId: "consolidation",
      toolCallName: "Create Consolidated Statements",
      preText:
        "All 12 IC transactions reconciled. I'll generate elimination entries in parallel across AR/AP, Revenue/COGS, and Mgmt Fees, then apply NCI and verify the trial balance.",
      postText:
        "12 elimination JEs generated — IC AR/AP $133K, Revenue/COGS $98.5K, Mgmt Fees $34K all net to $0. NCI adjustment: $4,200. Consolidated revenue: $2.4M. Net income: $312K. Trial balance verified. Ready for CFO sign-off.",
      preDurationMs: 3000,
      animDurationMs: 6000,
      postDurationMs: 4000,
      pauseMs: 1500,
    },
  ],

  getNodeColor: (nodeId: string) => {
    const map: Record<string, string> = {
      orchestrator: "var(--primary)",
      ingestion: "var(--ring)",
      detection: "var(--muted-foreground)",
      condition: "var(--color-neutral-500)",
      reconciler: "var(--muted-foreground)",
      "direct-post": "var(--muted-foreground)",
      consolidation: "var(--ring)",
    };
    return map[nodeId] ?? "var(--color-neutral-400)";
  },
};

export default AGENT_SCENARIO;
